import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAccess } from '../access.js';
import { api, usdFromMicros } from '../api.js';

export default function Dashboard() {
  const { can } = useAccess();
  const summaryQ = useQuery({
    queryKey: ['costs/summary'],
    queryFn: () => api.costSummary(),
    enabled: can('costs.read'),
  });
  const campaignsQ = useQuery({ queryKey: ['campaigns'], queryFn: () => api.listCampaigns() });

  const s = summaryQ.data;
  const campaigns = campaignsQ.data ?? [];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="text-slate-500 text-sm">Visão geral do tenant atual.</p>
        </div>
        {can('campaigns.write') && (
          <Link
            to="/campaigns/new"
            className="bg-brand-600 hover:bg-brand-700 text-white font-medium px-4 py-2 rounded-lg text-sm"
          >
            Nova campanha
          </Link>
        )}
      </div>

      {can('costs.read') && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Custo hoje" value={s ? usdFromMicros(s.todayMicros) : '—'} />
          <Stat label="Custo mês" value={s ? usdFromMicros(s.monthMicros) : '—'} />
          <Stat label="Leads" value={s?.leads ?? '—'} />
          <Stat label="Qualificados (≥70)" value={s?.qualified ?? '—'} />
          <Stat label="Cache hits" value={s?.cacheHits ?? '—'} />
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 text-sm font-medium text-slate-700">
          Campanhas
        </div>
        {campaignsQ.error ? (
          <p role="alert" className="p-6 text-red-600 text-sm">
            {campaignsQ.error.message}
          </p>
        ) : campaigns.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm">
            Nenhuma campanha ainda.{' '}
            {can('campaigns.write') && (
              <Link to="/campaigns/new" className="text-brand-700 underline">
                Criar a primeira
              </Link>
            )}
            .
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs uppercase bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2">Nome</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Estimado</th>
                <th className="text-right px-4 py-2">Gasto</th>
                <th className="text-right px-4 py-2">Criada</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/campaigns/${c.id}`} className="text-brand-700 underline">
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <StatusPill status={c.status} />
                  </td>
                  <td className="px-4 py-2 text-right">
                    {usdFromMicros(c.estimated_cost_microusd)}
                  </td>
                  <td className="px-4 py-2 text-right">{usdFromMicros(c.actual_cost_microusd)}</td>
                  <td className="px-4 py-2 text-right text-slate-500">
                    {new Date(c.created_at).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-slate-500 text-xs uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

export function StatusPill({ status }) {
  const labels = {
    draft: 'Rascunho',
    ready: 'Pronta',
    running: 'Em andamento',
    completed: 'Concluída',
    partial: 'Parcial',
    failed: 'Falhou',
    paused_budget: 'Pausada por limite de gasto',
    cancelled: 'Cancelada',
  };
  const map = {
    draft: 'bg-slate-100 text-slate-700',
    ready: 'bg-amber-100 text-amber-800',
    running: 'bg-blue-100 text-blue-800',
    completed: 'bg-emerald-100 text-emerald-800',
    partial: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
    paused_budget: 'bg-orange-100 text-orange-800',
    cancelled: 'bg-slate-200 text-slate-700',
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${map[status] ?? 'bg-slate-100 text-slate-700'}`}
    >
      {labels[status] ?? status}
    </span>
  );
}
